<template>
  <div class="control-center-page" :class="{ 'sidebar-open': sidebarOpen }">
    <div class="mobile-header">
      <button class="icon-btn menu-toggle-btn" @click="toggleSidebar">
        <i class="fas fa-bars"></i>
      </button>
      <div class="mobile-title-block">
        <h2>{{ $t('controlCenter.sidebar.title') }}</h2>
        <span>{{ activeMenuLabel }}</span>
      </div>
      <div class="mobile-topbar-actions">
        <el-dropdown trigger="click" @command="changeLanguage">
          <button class="icon-btn mobile-language-btn">
            <span>{{ currentLanguageFlag }}</span>
          </button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item
              v-for="lang in availableLanguages"
              :key="lang.code"
              :command="lang.code"
              :class="{ active: currentLanguage === lang.code }"
            >
              <span class="language-option">
                <span class="language-flag">{{ getLanguageFlag(lang.code) }}</span>
                <span>{{ lang.name }}</span>
              </span>
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        <button class="icon-btn mobile-exit-btn" :aria-label="$t('controlCenter.common.exitToHome')" @click="goHome">
          <i class="fas fa-right-from-bracket"></i>
        </button>
      </div>
    </div>

    <div class="sidebar-overlay" :class="{ active: sidebarOpen }" @click="closeSidebar"></div>

    <aside class="sidebar" :class="{ open: sidebarOpen }">
      <button class="sidebar-close-btn" @click="closeSidebar">
        <i class="fas fa-times"></i>
      </button>
      <div class="brand-block">
        <div class="brand-logo"><i class="fas fa-dove"></i></div>
        <div class="brand-meta">
          <div class="brand-title">{{ $t('controlCenter.sidebar.title') }}</div>
          <div class="brand-subtitle">{{ $t('controlCenter.sidebar.subtitle') }}</div>
        </div>
      </div>

      <div class="nav-group">
        <div
          v-for="item in visibleMenuItems"
          :key="item.key"
          class="nav-item"
          :class="{ active: activeTab === item.key }"
          @click="handleMenuSelect(item.key)"
        >
          <i :class="item.icon"></i>
          <span>{{ $t(item.label) }}</span>
        </div>
      </div>
    </aside>

    <main class="main-content">
      <div class="control-topbar">
        <div class="control-topbar-title">
          <span>{{ $t('controlCenter.sidebar.title') }}</span>
          <strong>{{ activeMenuLabel }}</strong>
        </div>
        <div class="control-topbar-actions">
          <el-dropdown trigger="click" @command="changeLanguage">
            <button class="control-language-btn">
              <span class="language-flag">{{ currentLanguageFlag }}</span>
              <span>{{ currentLanguageLabel }}</span>
              <i class="el-icon-arrow-down"></i>
            </button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item
                v-for="lang in availableLanguages"
                :key="lang.code"
                :command="lang.code"
                :class="{ active: currentLanguage === lang.code }"
              >
                <span class="language-option">
                  <span class="language-flag">{{ getLanguageFlag(lang.code) }}</span>
                  <span>{{ lang.name }}</span>
                </span>
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          <button class="control-exit-btn" @click="goHome">
            <i class="fas fa-right-from-bracket"></i>
            <span>{{ $t('controlCenter.common.exitToHome') }}</span>
          </button>
        </div>
      </div>

      <div v-if="authLoading" class="state-card">
        <i class="fas fa-circle-notch fa-spin"></i>
        <p>{{ $t('controlCenter.common.loading') }}</p>
      </div>

      <div v-else-if="!hasAdminAccess" class="state-card">
        <i class="fas fa-user-lock"></i>
        <h3>{{ $t('controlCenter.accessDenied.title') }}</h3>
        <p>{{ $t('controlCenter.accessDenied.description') }}</p>
      </div>

      <component v-else :is="activeComponent"></component>
    </main>
  </div>
</template>

<script>
import { getUserInfo, getToken, decodeJwtPayload } from '@/utils/auth'
import { userApi } from '@/api/user'
import Dashboard from './components/Dashboard.vue'
import PaymentDashboard from './components/PaymentDashboard.vue'
import CompanyEntitlements from './components/CompanyEntitlements.vue'
import CatalogManagement from './components/CatalogManagement.vue'
import OrderManagement from './components/OrderManagement.vue'
import InvoiceManagement from './components/InvoiceManagement.vue'
import UserManagement from './components/UserManagement.vue'
import AddOnManagement from './components/AddOnManagement.vue'
import MyProducts from './components/MyProducts.vue'
import PermissionManagement from './components/PermissionManagement.vue'
import TicketManagement from './components/TicketManagement.vue'
import AppManagement from './components/AppManagement.vue'
import AuditorSeatAssign from './components/AuditorSeatAssign.vue'
import AccountInfo from './components/AccountInfo.vue'

const FULL_ACCESS_USER_IDS = [
  '9a5ed52c-6fb0-4c7e-bfac-560fb3ebc09b',
  '0e229fce-6860-4491-9120-d04fb8fca007' // super.admin@lpt-software.com
]
const INVOICE_ONLY_USER_IDS = [
  'edf960e5-829a-4083-9e7a-a4b223ca61e9',
  'd1dee32f-4f63-43f7-930e-5c48baac6a05'
]
const DEFAULT_DENIED_MENU_KEYS = ['paymentDashboard', 'invoices', 'catalog']

export default {
  name: 'ControlCenter',
  components: {
    Dashboard,
    PaymentDashboard,
    CompanyEntitlements,
    CatalogManagement,
    OrderManagement,
    InvoiceManagement,
    UserManagement,
    AddOnManagement,
    MyProducts,
    PermissionManagement,
    TicketManagement,
    AppManagement,
    AuditorSeatAssign,
    AccountInfo
    },
  data() {
    return {
      activeTab: 'dashboard',
      sidebarOpen: false,
      authLoading: true,
      hasAdminAccess: false,
      currentUser: null,
      cachedUserInfo: null,
      currentLanguage: 'zhCN',
      languageFlags: {
        en: '🇺🇸',
        fr: '🇫🇷',
        es: '🇪🇸',
        zhCN: '🇨🇳',
        de: '🇩🇪'
      },
      menuItems: [
        {
          key: 'dashboard',
          label: 'controlCenter.sidebar.dashboard',
          icon: 'fas fa-chart-line'
        },
        {
          key: 'paymentDashboard',
          label: 'controlCenter.sidebar.paymentDashboard',
          icon: 'fas fa-credit-card'
        },
        {
          key: 'companyEntitlements',
          label: 'controlCenter.sidebar.companyEntitlements',
          icon: 'fas fa-building-shield',
          superAdminOnly: true
        },
        {
          key: 'appManagement',
          label: 'controlCenter.sidebar.appManagement',
          icon: 'fas fa-cube',
          // 暂时隐藏菜单入口，仍可通过 /controlCenter?tab=appManagement 直接访问，后续开放时移除该标记
          hidden: true
        },
        {
          key: 'accountInfo',
          label: 'controlCenter.sidebar.accountInfo',
          icon: 'fas fa-user-circle'
        },
        {
          key: 'catalog',
          label: 'controlCenter.sidebar.catalog',
          icon: 'fas fa-tags'
        },
        {
          key: 'myProducts',
          label: 'controlCenter.sidebar.myProducts',
          icon: 'fas fa-box-open'
        },
        {
          key: 'addOns',
          label: 'controlCenter.sidebar.addOnManagement',
          icon: 'fas fa-cubes'
        },
        {
          key: 'orders',
          label: 'controlCenter.sidebar.orders',
          icon: 'fas fa-receipt'
        },
        {
          key: 'invoices',
          label: 'controlCenter.sidebar.invoices',
          icon: 'fas fa-file-invoice'
        },
        {
          key: 'tickets',
          label: 'controlCenter.sidebar.tickets',
          icon: 'fas fa-headset'
        },
        {
          key: 'users',
          label: 'controlCenter.sidebar.userManagement',
          icon: 'fas fa-users'
        },
        {
          key: 'permissions',
          label: 'controlCenter.sidebar.permissionManagement',
          icon: 'fas fa-user-shield'
        }
      ]
    }
  },
  computed: {
    activeComponent() {
      return this.componentForTab(this.allowedActiveTab)
    },
    visibleMenuItems() {
      if (!this.hasAdminAccess) {
        return []
      }
      return this.menuItems.filter(item => !item.hidden && this.canAccessMenuItem(item))
    },
    allowedActiveTab() {
      // 分配席位是独立子页面 tab，不参与菜单可见性检查
      if (this.activeTab === 'auditorSeatAssign') return 'auditorSeatAssign'
      // 隐藏入口的 tab（如 appManagement）仍允许通过 URL query 直接访问
      if (this.menuItems.some(item => item.key === this.activeTab && this.canAccessMenuItem(item))) {
        return this.activeTab
      }
      return this.visibleMenuItems.some(item => item.key === this.activeTab)
        ? this.activeTab
        : this.firstVisibleMenuKey
    },
    activeMenuLabel() {
      const activeItem = this.menuItems.find(item => item.key === this.allowedActiveTab)
      return activeItem ? this.$t(activeItem.label) : this.$t('controlCenter.sidebar.title')
    },
    firstVisibleMenuKey() {
      return this.visibleMenuItems[0] ? this.visibleMenuItems[0].key : 'dashboard'
    },
    currentUserIds() {
      const cached = this.cachedUserInfo || getUserInfo() || {}
      const remote = this.currentUser || {}
      // 以当前 token 的 JWT payload 为准，避免 localStorage 中残留旧账号的 userInfo
      let tokenIds = {}
      try {
        const payload = decodeJwtPayload(getToken())
        tokenIds = payload ? { userId: payload.userId, sub: payload.sub } : {}
      } catch (e) {
        tokenIds = {}
      }
      return [
        cached.userId,
        cached.id,
        cached.sub,
        remote.userId,
        remote.id,
        remote.sub,
        tokenIds.userId,
        tokenIds.sub
      ]
        .filter(Boolean)
        .map(value => String(value).trim().toLowerCase())
        .filter((value, index, values) => values.indexOf(value) === index)
    },
    hasFullMenuAccess() {
      return this.currentUserIds.some(userId => FULL_ACCESS_USER_IDS.includes(userId))
    },
    hasInvoiceOnlyAccess() {
      return this.currentUserIds.some(userId => INVOICE_ONLY_USER_IDS.includes(userId))
    },
    hasSuperAdminAccess() {
      return this.isSuperAdminUser(this.currentUser) || this.isSuperAdminUser(getUserInfo())
    },
    availableLanguages() {
      return [
        { code: 'en', name: this.$t('language.en') || 'English' },
        { code: 'fr', name: this.$t('language.fr') || 'French' },
        { code: 'es', name: this.$t('language.es') || 'Spanish' },
        { code: 'zhCN', name: this.$t('language.zh') || 'Chinese' },
        { code: 'de', name: this.$t('language.de') || 'German' }
      ]
    },
    currentLanguageLabel() {
      const lang = this.availableLanguages.find(item => item.code === this.currentLanguage)
      return lang ? lang.name : this.$t('language.zh')
    },
    currentLanguageFlag() {
      return this.getLanguageFlag(this.currentLanguage)
    }
  },
  watch: {
    visibleMenuItems(items) {
      // 独立子页面 tab 不应被菜单变化回退
      if (this.activeTab === 'auditorSeatAssign') return
      // 隐藏入口的 tab（如 appManagement）仍允许通过 URL query 直接访问，不回退
      if (this.menuItems.some(item => item.key === this.activeTab && this.canAccessMenuItem(item))) return
      if (!items.some(item => item.key === this.activeTab)) {
        this.activeTab = this.firstVisibleMenuKey
      }
    },
    '$route.query.tab'() {
      this.initActiveTabFromRoute()
    }
  },
  created() {
    this.initLanguage()
    this.initActiveTabFromRoute()
    this.resolveAccess()
  },
  methods: {
    initLanguage() {
      const savedLanguage = localStorage.getItem('lang') || 'zhCN'
      this.currentLanguage = savedLanguage === 'zh' ? 'zhCN' : savedLanguage
      this.$i18n.locale = this.currentLanguage
    },
    changeLanguage(languageCode) {
      const targetLang = languageCode === 'zh' ? 'zhCN' : languageCode
      this.currentLanguage = targetLang
      this.$i18n.locale = targetLang
      localStorage.setItem('lang', targetLang)
    },
    getLanguageFlag(code) {
      return this.languageFlags[code] || '🌐'
    },
    initActiveTabFromRoute() {
      const query = (this.$route && this.$route.query) || {}
      const tab = Array.isArray(query.tab) ? query.tab[0] : query.tab
      const hasPaymentReturn = query.add_on_checkout === '1' ||
        query.payment_intent_client_secret ||
        query.checkout_session_id ||
        query.alipay_return === '1' ||
        query.payment_method === 'alipay'
      let targetTab = null
      if (hasPaymentReturn) {
        targetTab = 'addOns'
      } else if (tab === 'auditorSeatAssign') {
        targetTab = 'auditorSeatAssign'
      } else if (this.menuItems.some(item => item.key === tab)) {
        targetTab = tab
      }
      if (!targetTab) return

      if (!this.hasAdminAccess) {
        this.activeTab = targetTab
        return
      }
      // 分配席位是独立子页面 tab，不参与菜单可见性检查
      if (targetTab === 'auditorSeatAssign') {
        this.activeTab = 'auditorSeatAssign'
        return
      }
      // 隐藏入口的 tab（如 appManagement）仍允许通过 URL query 直接访问
      this.activeTab = this.menuItems.some(item => item.key === targetTab && this.canAccessMenuItem(item))
        ? targetTab
        : this.firstVisibleMenuKey
    },
    async resolveAccess() {
      this.authLoading = true
      const cachedUserInfo = getUserInfo() || {}
      this.cachedUserInfo = cachedUserInfo
      const userId = cachedUserInfo.userId || cachedUserInfo.id || cachedUserInfo.sub
      if (!userId) {
        this.hasAdminAccess = false
        this.authLoading = false
        return
      }

      try {
        const response = await userApi.getUser(userId)
        const currentUser = response && response.data ? response.data : response
        this.currentUser = currentUser
        this.hasAdminAccess = true
      } catch (error) {
        console.error('Failed to resolve control center access:', error)
        this.hasAdminAccess = true
      } finally {
        this.authLoading = false
        this.initActiveTabFromRoute()
      }
    },
    handleMenuSelect(tab) {
      if (!this.visibleMenuItems.some(item => item.key === tab)) {
        return
      }
      this.activeTab = tab
      // 同步 URL query 参数，避免 URL 与 activeTab 状态不一致
      // 否则后续通过 $router.push 跳转到相同 tab 时会触发 NavigationDuplicated 而被静默吞掉
      const currentTab = this.$route.query.tab
      if (currentTab !== tab) {
        this.$router.replace({
          path: '/controlCenter',
          query: { tab }
        }).catch(() => {})
      }
      if (window.innerWidth <= 768) {
        this.closeSidebar()
      }
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen
    },
    closeSidebar() {
      this.sidebarOpen = false
    },
    goHome() {
      this.closeSidebar()
      this.$router.push('/').catch(() => {})
    },
    canAccessMenuItem(item) {
      if (!item) return false
      if (this.hasFullMenuAccess) return true
      if (this.hasInvoiceOnlyAccess) return item.key === 'invoices'
      if (item.superAdminOnly && !this.hasSuperAdminAccess) return false
      return !DEFAULT_DENIED_MENU_KEYS.includes(item.key)
    },
    componentForTab(tab) {
      if (tab === 'paymentDashboard') {
        return 'PaymentDashboard'
      }
      if (tab === 'companyEntitlements') {
        return 'CompanyEntitlements'
      }
      if (tab === 'appManagement') {
        return 'AppManagement'
      }
      if (tab === 'auditorSeatAssign') {
        return 'AuditorSeatAssign'
      }
      if (tab === 'catalog') {
        return 'CatalogManagement'
      }
      if (tab === 'addOns') {
        return 'AddOnManagement'
      }
      if (tab === 'myProducts') {
        return 'MyProducts'
      }
      if (tab === 'orders') {
        return 'OrderManagement'
      }
      if (tab === 'invoices') {
        return 'InvoiceManagement'
      }
      if (tab === 'tickets') {
        return 'TicketManagement'
      }
      if (tab === 'users') {
        return 'UserManagement'
      }
      if (tab === 'permissions') {
        return 'PermissionManagement'
      }
      if (tab === 'accountInfo') {
        return 'AccountInfo'
      }
      return 'Dashboard'
    },
    isSuperAdminUser(user) {
      if (!user) return false
      const values = [
        user.type,
        user.role,
        user.accountType,
        user.userType
      ]
      const collections = [
        user.permissions,
        user.permissionSets,
        user.roles,
        user.authorities
      ]
      collections.forEach(collection => {
        if (Array.isArray(collection)) {
          collection.forEach(item => {
            if (typeof item === 'string') {
              values.push(item)
            } else if (item && typeof item === 'object') {
              values.push(item.code, item.name, item.key)
            }
          })
        }
      })
      return values
        .filter(Boolean)
        .map(value => String(value).trim().toUpperCase())
        .some(value => ['SUPER_ADMIN', 'SUPER_ADMINISTRATOR', 'PLATFORM_ADMIN', 'ROOT'].includes(value))
    }
  }
}
</script>

<style lang="scss">
@import './styles.scss';

.control-center-page {
  display: flex;
  min-height: 100vh;
  background: linear-gradient(180deg, #f4f8fc 0%, #eef4fb 100%);
}

.mobile-header {
  display: none;
}

.sidebar-overlay {
  display: none;
}

.sidebar-close-btn {
  display: none;
}

.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  width: 280px;
  height: 100vh;
  height: 100dvh;
  padding: 20px 14px;
  background: linear-gradient(135deg, rgba(6, 73, 184, 0.96) 0%, rgba(10, 127, 215, 0.92) 52%, rgba(3, 210, 197, 0.88) 100%);
  color: #fff;
  box-shadow: 12px 0 40px rgba(15, 23, 42, 0.16);
  overflow-y: auto;
  z-index: 900;
}

.brand-block {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
  padding: 16px 18px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.05);
}

.brand-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.24);
  color: #fff;
  font-size: 18px;
  box-shadow: 0 6px 16px rgba(6, 73, 184, 0.28);
}

.brand-meta {
  min-width: 0;
}

.brand-title {
  font-size: 18px;
  font-weight: 700;
  color: #f8fafc;
}

.brand-subtitle {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.75);
}

.nav-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.nav-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 14px;
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.82);
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
  -webkit-tap-highlight-color: transparent;

  i {
    width: 18px;
    text-align: center;
    color: rgba(255, 255, 255, 0.65);
    transition: color 0.2s ease;
  }

  span {
    font-size: 14px;
    font-weight: 500;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
  }

  &:active {
    background: rgba(255, 255, 255, 0.12);
  }

  &.active {
    background: rgba(255, 255, 255, 0.16);
    color: #fff;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.14);

    i {
      color: #ffffff;
    }

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 24px;
      border-radius: 999px;
      background: #5eead4;
    }
  }
}

.main-content {
  flex: 1;
  min-width: 0;
  margin-left: 280px;
  padding: 32px 30px;
}

.control-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
  padding: 14px 16px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.86);
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.07);
  backdrop-filter: blur(10px);
}

.control-topbar-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  span {
    color: #64748b;
    font-size: 12px;
    font-weight: 600;
  }

  strong {
    color: #0f172a;
    font-size: 18px;
    font-weight: 800;
    line-height: 1.2;
  }
}

.control-topbar-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex: 0 0 auto;
}

.control-language-btn,
.control-exit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 38px;
  padding: 0 14px;
  border: 1px solid rgba(59, 130, 246, 0.22);
  border-radius: 12px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(37, 99, 235, 0.36);
    background: #dbeafe;
    transform: translateY(-1px);
  }
}

.control-language-btn {
  border-color: rgba(148, 163, 184, 0.24);
  background: #fff;
  color: #334155;
}

.language-option {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.language-flag {
  line-height: 1;
}

.el-dropdown-menu__item.active {
  color: #1d4ed8;
  font-weight: 700;
}

.state-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 360px;
  padding: 40px 24px;
  border-radius: 24px;
  background: #fff;
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.08);
  text-align: center;

  i {
    font-size: 28px;
    color: #0a7fd7;
  }

  h3 {
    margin: 0;
    font-size: 22px;
    color: #0f172a;
  }

  p {
    max-width: 420px;
    margin: 0;
    color: #64748b;
    line-height: 1.7;
  }
}

@media screen and (max-width: 1100px) and (min-width: 769px) {
  .sidebar {
    width: 236px;
    padding: 18px 12px;
  }

  .brand-block {
    padding: 15px;
  }

  .brand-title {
    font-size: 16px;
  }

  .brand-subtitle {
    font-size: 11px;
  }

  .nav-item {
    gap: 10px;
    padding: 12px;

    span {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .main-content {
    margin-left: 236px;
    padding: 26px 22px;
  }

  .control-topbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .control-topbar-actions {
    width: 100%;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
}

@media screen and (max-width: 768px) {
  .control-center-page.sidebar-open {
    overflow: hidden;
  }

  .mobile-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1100;
    height: calc(60px + env(safe-area-inset-top));
    padding: env(safe-area-inset-top) 12px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    background: linear-gradient(135deg, rgba(6, 73, 184, 0.96) 0%, rgba(10, 127, 215, 0.92) 52%, rgba(3, 210, 197, 0.88) 100%);
    color: #fff;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.18);
    border-bottom: 1px solid rgba(255, 255, 255, 0.14);

    h2 {
      margin: 0;
      font-size: 11px;
      font-weight: 600;
      line-height: 1.2;
      letter-spacing: 0.06em;
      color: rgba(255, 255, 255, 0.72);
    }
  }

  .icon-btn {
    border: none;
    background: rgba(255, 255, 255, 0.1);
    color: inherit;
    font-size: 18px;
    cursor: pointer;
    transition: background-color 0.15s ease, transform 0.15s ease;
    -webkit-tap-highlight-color: transparent;

    &:active {
      background: rgba(255, 255, 255, 0.18);
      transform: scale(0.94);
    }
  }

  .menu-toggle-btn {
    width: 44px;
    height: 44px;
    flex: 0 0 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
  }

  .mobile-title-block {
    min-width: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 3px;
    text-align: center;

    span {
      display: block;
      max-width: 100%;
      color: #f8fafc;
      font-size: 16px;
      font-weight: 700;
      line-height: 1.25;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .mobile-exit-btn {
    width: 44px;
    flex: 0 0 44px;
    height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
  }

  .mobile-topbar-actions {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
  }

  .mobile-language-btn {
    width: 44px;
    height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    font-size: 18px;
  }

  .sidebar-overlay {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.42);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
    z-index: 1000;

    &.active {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .sidebar {
    top: 0;
    width: min(312px, 88vw);
    height: 100vh;
    height: 100dvh;
    padding: 18px 14px calc(24px + env(safe-area-inset-bottom));
    transform: translateX(-100%);
    transition: transform 0.22s ease;
    z-index: 1050;

    &.open {
      transform: translateX(0);
    }
  }

  .sidebar-close-btn {
    position: absolute;
    top: 16px;
    right: 14px;
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(226, 232, 240, 0.16);
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.08);
    color: #f8fafc;
    cursor: pointer;
    transition: background-color 0.15s ease;
    -webkit-tap-highlight-color: transparent;

    &:active {
      background: rgba(255, 255, 255, 0.18);
    }
  }

  .brand-block {
    margin-right: 48px;
    border-radius: 14px;
    padding-bottom: 16px;
    border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  }

  .nav-group {
    gap: 6px;
    padding-bottom: 14px;
  }

  .nav-item {
    min-height: 46px;
    padding: 12px 13px;
    border-radius: 11px;

    span {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .main-content {
    margin-left: 0;
    width: 100%;
    padding: calc(88px + env(safe-area-inset-top)) 16px 24px;
  }

  .control-topbar {
    display: none;
  }
}
</style>
